const _ = require("lodash");
const moment = require("moment");

const AT = require("./ActionTypes");
const OpsBlock = require("./OpsBlock");
const OpsTempUser = require("./OpsTempUser");
const OpsUser = require("./OpsUser");
const APIFormat = require("./util/APIFormat");
const LoadState = require("./util/LoadState");
const ChapterSection = require("../../../shared/ChapterSection");
const GradeChapter = require("../../../shared/GradeChapter");
const PageShared = require("../../../shared/PageShared");
const dbDemoData = require("../../../shared/data/dbDemoData.json");
const URLMake = require("../../../shared/URLMake");

const DEMO_DATA_STORED_VERSION = 8;

const {
	atBlocks,
	atChapter,
	atCurrProblem,
	atDemo,
	atDemoData,
	atPage,
	atTempUser,
} = require("./StateCursors");
const {setIn, extendIn} = require("../../../shared/ImmHelpers");
const {getFeatureFlag} = require("../util/FeatureFlags");

// Collapsed Library+Theater remaps /theater → library.grade + tab:videos.
// Demo tutorials / sell-modal still key off theater.chapter vs library.grade.
const getDemoTutorialPageKey = function(page) {
	if (
		getFeatureFlag("ENABLE_COLLAPSED_LIBRARY_AND_THEATER") &&
		page &&
		page.name === "library.grade"
	) {
		return page.tab === "videos" ? "theater.chapter" : "library.grade";
	}
	return page && page.name;
};

const isDemoPage = function(state) {
	const page = atPage(state);
	return page.getIn("demo") && PageShared.isValidDemoPage(page.get());
};

const checkDemoRestriction = function(demoData, page, loggedIn) {
	if (loggedIn || !demoData) return page;
	const {gradeNumber, chapterIndex, blockIndex} = page;
	const chapterID = GradeChapter.toChapterID(gradeNumber, chapterIndex);

	// Send user to demo because they shouldn't have access to non-demo content
	if (page.name === "trainer.problem") {
		const trainerBlockID = _.get(demoData, [
			"loadChapterResponse",
			chapterID,
			"blockIDs",
			blockIndex,
		]);
		// if trying to load a non-demo block, redirect to demo classroom
		if (!_.includes(demoData.blockIDs, trainerBlockID)) {
			return URLMake.state.forDemoChapter();
		}
	} else if (page.name === "theater.set") {
		const mappedVideoBlockIndex = _.find(
			demoData.theaterChapterVideoBlockMapping,
			m => m.chapterID === chapterID
		);
		if (
			!mappedVideoBlockIndex ||
			mappedVideoBlockIndex.blockIndex !== blockIndex
		) {
			return URLMake.state.forDemoTheaterChapter();
		}
	} else if (page.name === "library.chapter") {
		if (!chapterID || !demoData.chapterPages[chapterID]) {
			return URLMake.state.forDemoLibraryGrade();
		}
	}
	return page;
};

// Used as a postUpdateHook so for every action we check for demo information to update and
// events to fire.
const demoPostUpdateHook = function(vault, oldState, newState, action) {
	// Do nothing if the call isn't for demo
	if (!isDemoPage(newState) || action.type === AT.STORAGE_UPDATED)
		return newState;

	const oldPage = atPage(oldState).get();
	const newPage = atPage(newState).get();
	const now = moment.now();
	const currentProblemOutcome =
		(action.type === AT.SAVE_TRIAL_STATE || action.type === AT.SUBMIT_TRIAL) &&
		atCurrProblem(newState).getIn("outcome");
	const isOnTrophyPage =
		action.type === AT.SET_PAGE &&
		_.get(action, ["pageState", "problemIndex"]) === "trophy";
	const storage = vault.storage;

	// Get demo-specific data
	newState = loadDemoDataIfNeeded(newState, storage);

	// Get temp user data
	newState = OpsTempUser.getTempUser(
		newState,
		storage,
		"demo",
		DEMO_DATA_STORED_VERSION
	);

	newState = atDemo(newState).set(demo => {
		const showTutorial = type => {
			demo = setIn(demo, "lastShownAt", now);
			demo = setIn(demo, "showing", type);
		};

		const setSounds = () => {
			vault.sounds.setAllSoundsMuted(
				!OpsUser.areSoundsEnabled(atTempUser(newState).get())
			);
			vault.sounds.setMusicMuted(
				!OpsUser.isMusicEnabled(atTempUser(newState).get())
			);
			vault.sounds.setAllSoundsVolume(
				OpsUser.getSoundsVolume(atTempUser(newState).get())
			);
			vault.sounds.setMusicVolume(
				OpsUser.getMusicVolume(atTempUser(newState).get())
			);
		};

		if (!demo.dataVersion) {
			demo = setIn(demo, "dataVersion", DEMO_DATA_STORED_VERSION);
		}

		// Set time stamp for when first demo page entered
		if (!demo.begunAt) {
			demo = setIn(demo, "begunAt", now);
			// reset the sound when starting or restarting demo
			setSounds();
		}

		// Reload sound settings when page refreshed
		if (action.type === AT.TRAINER_INITIAL) {
			setSounds();
		}

		// Set time stamp for when user began viewing book chapter
		if (action.type === AT.SET_VIEWED_LIBRARY_PAGE) {
			if (action.chapterID !== _.get(demo, "lastViewedBookChapter.chapterID")) {
				demo = extendIn(demo, "lastViewedBookChapter", {
					chapterID: action.chapterID,
					timestamp: moment.now(),
					pageViewed: action.pageNumber,
				});
			}
		}

		// Wipe timestamp for when user began viewing book chapter
		if (
			(action.type === AT.SET_SHOW_BOOK && action.showBook === false) ||
			(oldPage &&
				oldPage.name === "library.chapter" &&
				oldPage.name !== newPage.name)
		) {
			demo = setIn(demo, "lastViewedBookChapter", null);
		}

		// if going to a new page, clear the tutorial modal shown
		const oldTutorialKey = getDemoTutorialPageKey(oldPage);
		const newTutorialKey = getDemoTutorialPageKey(newPage);
		if (!oldPage || oldTutorialKey !== newTutorialKey) {
			demo = setIn(demo, "showing", null);
			demo = setIn(demo, "isShowingEmailModal", null);
		}

		const isShowingEmailModal = demo.isShowingEmailModal;

		if (
			(!demo.tutorialExited &&
				(!oldPage || oldTutorialKey !== newTutorialKey)) ||
			(action.type === AT.RESTART_DEMO && oldPage.name === "home.main")
		) {
			// MOVING TO NEW TRACKED PAGE
			const visitCount = _.get(demo, ["pageVisits", newTutorialKey]);
			const showSellModalOnPage = function(pageName) {
				return (
					demo.sellModalTriggered &&
					!demo.sellModalShown &&
					_.includes(demo.showSellModalOn, pageName)
				);
			};

			// Take relevant page load tutorial action
			if (newTutorialKey === "home.main") {
				if (!visitCount) {
					showTutorial("home1stVisit");
				} else if (visitCount === 1) {
					showTutorial("home2ndVisit");
				}
			}

			if (newTutorialKey === "trainer.chapter") {
				if (!visitCount) {
					showTutorial("trainerChapter");
				} else if (showSellModalOnPage("trainer.chapter")) {
					showTutorial("sellModal");
					demo = setIn(demo, "sellModalShown", true);
				}
			}

			if (newTutorialKey === "library.grade") {
				if (!visitCount) {
					showTutorial("libraryGrade");
				} else if (showSellModalOnPage("library.grade")) {
					showTutorial("sellModal");
					demo = setIn(demo, "sellModalShown", true);
				}
			}

			if (newTutorialKey === "library.chapter" && !visitCount) {
				showTutorial("libraryChapter");
			}

			if (newTutorialKey === "theater.chapter") {
				if (!visitCount) {
					showTutorial("theaterChapter");
				} else if (showSellModalOnPage("theater.chapter")) {
					showTutorial("sellModal");
					demo = setIn(demo, "sellModalShown", true);
				}
			}

			if (newTutorialKey === "profile.main" && !visitCount) {
				showTutorial("profile");
			}

			if (!_.get(demo, ["pageVisits", "trophy"]) && isOnTrophyPage) {
				demo = setIn(demo, ["pageVisits", "trophy"], true);
				showTutorial("trophy");
			}

			// increment page visit count
			demo = setIn(demo, ["pageVisits", newTutorialKey], (visitCount || 0) + 1);
		} else if (action.type === AT.VIEW_VIDEO_UNDER_CONSTRUCTION) {
			showTutorial("videoUnderConstruction");
		} else if (
			!demo.tutorialExited &&
			!_.get(demo, "actions.problemCompleted") &&
			currentProblemOutcome &&
			!newPage.name.includes("theater")
		) {
			// FIRST PROBLEM COMPLETED
			demo = setIn(demo, "actions.problemCompleted", true);
			showTutorial(
				currentProblemOutcome === "correct"
					? "problemCorrect"
					: "problemIncorrect"
			);
		} else if (
			!demo.tutorialExited &&
			action.type === AT.DEMO_SLIDER_OPENED &&
			!_.get(demo, "actions.sliderOpened")
		) {
			// FIRST TIME SLIDER OPENED
			demo = setIn(demo, "actions.sliderOpened", true);
			showTutorial("sliderOpened");
		} else if (
			!demo.tutorialExited &&
			action.type === AT.ATTEMPT_REPLAY_DEMO_LESSON &&
			!_.get(demo, "actions.replayLesson")
		) {
			// FIRST TIME SLIDER OPENED
			demo = setIn(demo, "actions.replayLesson", action.blockID);
			showTutorial("replayLesson");
		} else if (
			!demo.sellModalShown &&
			!isShowingEmailModal &&
			now - demo.begunAt >= 420000 &&
			!demo.sellModalTriggered
		) {
			// SELL MODAL
			let triggered;
			const showOn = demo.showSellModalOn || [];

			if (action.type === AT.GOTO_FINAL) {
				// FINISHED LESSON
				triggered = true;
				showOn.push("trainer.chapter");
			} else if (action.type === AT.VIDEO_FINISHED) {
				// FINISHED VIDEO
				triggered = true;
				showOn.push("theater.chapter");
			} else if (
				demo.lastViewedBookChapter &&
				now - demo.lastViewedBookChapter.timestamp > 120000 &&
				demo.lastViewedBookChapter.pageViewed ===
					ChapterSection.getChapterPages(
						demo.lastViewedBookChapter.chapterID,
						true
					)[1]
			) {
				// FINISHED BOOK SECTION AND SPENT MORE THAN 2 MINUTE THERE
				triggered = true;
				showOn.push("library.grade");
			}

			demo = setIn(demo, "sellModalTriggered", !!triggered);
			demo = setIn(demo, "showSellModalOn", showOn);
		}

		return demo;
	});

	newState = saveLocalDemoData(newState, storage);
	// Check for errored chapters and reload as necessary
	_.each(
		_.omit(atTempUser(newState).getIn("chapters"), ["load", "loadError"]),
		chapterObj => {
			if (_.get(chapterObj, "load") === "error")
				newState = loadDemoChapter(newState, chapterObj.chapterID);
		}
	);

	return newState;
};

const loadDemoDataIfNeeded = (state, storage) => {
	// bail if data already exists
	if (!_.isEmpty(atDemo(state).get())) {
		return state;
	}
	// attempt to load from local storage
	state = loadLocalDemoData(state, storage);
	if (!_.isEmpty(atDemo(state).get())) {
		return state;
	}
	// now load from file
	state = atDemoData(state).set(dbDemoData);
	return state;
};

// Returns a state object with data from storage possibly loaded into atDemo.
const loadLocalDemoData = function(state, storage) {
	const currentDemoData = atDemo(state).get();
	if (!_.isEmpty(currentDemoData)) {
		return state;
	}
	const localDemoDataJSON = storage.getItem("demoData");
	if (!localDemoDataJSON) {
		return state;
	}
	const localDemoData = JSON.parse(localDemoDataJSON);
	const localDemoVersion = _.get(localDemoData, "dataVersion", 1);
	if (localDemoVersion < DEMO_DATA_STORED_VERSION) {
		storage.removeItem("demoData");
		return state;
	}
	return atDemo(state).set(localDemoData);
};

const saveLocalDemoData = function(state, storage) {
	let demo = atDemo(state).get();
	// clear load state, as they're meant to be used per page load
	const demoData = _.get(demo, "data");
	if (demoData && demoData.load !== "done") demo = setIn(demo, "data", null);
	storage.setItem("demoData", JSON.stringify(demo));
	return OpsTempUser.saveLocalTempUserData(state, storage);
};

const resetDemo = function(state, storage) {
	state = atDemo(state).set({});
	storage.removeItem("demoData");
	return OpsTempUser.clearTempUser(state, storage);
};

const exitTutorial = function(state, storage) {
	state = atDemo(state).set(demo => {
		demo.tutorialExited = true;
		return demo;
	});
	return state;
};

const getVideoBlockGradeNumber = function(block) {
	const context = GradeChapter.chapterIDToContext(block.chapterID);
	return context.gradeNumber;
};

const loadDemoChapter = function(state, chapterID) {
	// set the chapter model and block models here
	const demoData = atDemoData(state).get();
	if (demoData?.load !== "done") return state;
	const response = demoData.loadChapterResponse[chapterID];
	const chapterModel = APIFormat.convertChapter(chapterID, response);
	const blockModels = APIFormat.convertChapterBlocks(chapterID, response);

	state = atChapter(state, chapterID)
		.canCreate()
		.set(chapter => {
			chapter = LoadState.loadSuccess(chapter);
			return setIn(chapter, "model", chapterModel);
		});

	const blockIDs = Object.keys(blockModels);
	state = atBlocks(state)
		.canCreate()
		.setKeys(blockIDs, (bl, id) => {
			bl = bl || OpsBlock.init(parseInt(id));
			return setIn(bl, "model", blockModels[id]);
		});

	return state;
};

module.exports = {
	isDemoPage,
	checkDemoRestriction,
	demoPostUpdateHook,
	saveLocalDemoData,
	resetDemo,
	exitTutorial,
	getVideoBlockGradeNumber,
	loadDemoChapter,
};
